import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';
import { Box, Card, Typography, useMediaQueries } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { useStore } from '~/contexts';
import { useLocaleKeys } from '~/i18n';
import { queryClient } from '~/queryClient';
import { selectors } from '~/store/state-selectors';
import { useStoreActions } from '~/store/store-context';
import { Accounting } from '~/types/api.model';
import { OnboardingLayout } from './components/OnboardingLayout/OnboardingLayout';
import { BrandStep } from './components/steps/BrandStep/BrandStep';
import { computeInitialBranding, computeInitialLogoDataURL } from './components/steps/BrandStep/hooks/useBrandStepForm';
import { BusinessDetailsAStep } from './components/steps/BusinessDetailsAStep/BusinessDetailsAStep';
import { BusinessDetailsBStep } from './components/steps/BusinessDetailsBStep/BusinessDetailsBStep';
import { ClosingStep } from './components/steps/ClosingStep/ClosingStep';
import { IntegrationsStep } from './components/steps/IntegrationsStep/IntegrationsStep';
import { IntentStep } from './components/steps/IntentStep/IntentStep';
import { IntroStep } from './components/steps/IntroStep/IntroStep';
import { createEmptyMember, InviteTeamStep } from './components/steps/InviteTeamStep/InviteTeamStep';
import { QboXeroIntegrationStep } from './components/steps/QboXeroIntegrationStep/QboXeroIntegrationStep';
import { SecureLoginStep } from './components/steps/SecureLoginStep/SecureLoginStep';
import { WaitingStep } from './components/steps/WaitingStep/WaitingStep';
import { WebsiteStep } from './components/steps/WebsiteStep/WebsiteStep';
import { clearPersistedOnboardingProgress, persistCreditsAwarded, persistFormState, readPersistedCreditsAwarded, readPersistedFormState, useOnboardingFlow } from './hooks/useOnboardingFlow';
import styles from './VendorOnboarding.module.scss';
const EMPTY_POSTAL_ADDRESS = {
    address1: '',
    address2: '',
    city: '',
    state: '',
    postalCode: '',
    country: 'US'
};
export const GetVendorOnboardingTID = (parentTestId)=>getTestIds({
        parentTestId
    });
const DEFAULT_BUSINESS_DETAILS_A = {
    companyName: '',
    postalAddress: EMPTY_POSTAL_ADDRESS,
    phoneNumber: ''
};
const DEFAULT_BUSINESS_DETAILS_B = {
    businessDescription: '',
    numInvoices: 'numInvoices20To50',
    teamSize: '2_to_5'
};
export const VendorOnboarding = (param)=>{
    let { testId, mode, onComplete, onClose } = param;
    var _ref, _ref1, _ref2, _ref3, _ref4, _ref5, _ref6, _useStore, _ref7, _ref8, _ref9, _ref10, _ref11, _ref12, _ref13;
    const testIds = useMemo(()=>GetVendorOnboardingTID(testId), [
        testId
    ]);
    const { t: texts } = useLocaleKeys('vendorOnboarding');
    const persistedFormState = useMemo(()=>readPersistedFormState(), []);
    const [businessDetailsA, setBusinessDetailsA] = useState((_ref = persistedFormState === null || persistedFormState === void 0 ? void 0 : persistedFormState.businessDetailsA) !== null && _ref !== void 0 ? _ref : DEFAULT_BUSINESS_DETAILS_A);
    const [businessDetailsB, setBusinessDetailsB] = useState((_ref1 = persistedFormState === null || persistedFormState === void 0 ? void 0 : persistedFormState.businessDetailsB) !== null && _ref1 !== void 0 ? _ref1 : DEFAULT_BUSINESS_DETAILS_B);
    const [intent, setIntent] = useState((_ref2 = persistedFormState === null || persistedFormState === void 0 ? void 0 : persistedFormState.intent) !== null && _ref2 !== void 0 ? _ref2 : {
        purposes: []
    });
    const [inviteTeam, setInviteTeam] = useState((_ref3 = persistedFormState === null || persistedFormState === void 0 ? void 0 : persistedFormState.inviteTeam) !== null && _ref3 !== void 0 ? _ref3 : {
        members: [
            createEmptyMember()
        ]
    });
    const [integrations, setIntegrations] = useState((_ref4 = persistedFormState === null || persistedFormState === void 0 ? void 0 : persistedFormState.integrations) !== null && _ref4 !== void 0 ? _ref4 : {
        selectedTools: [],
        otherTools: []
    });
    const [website, setWebsite] = useState((_ref5 = persistedFormState === null || persistedFormState === void 0 ? void 0 : persistedFormState.website) !== null && _ref5 !== void 0 ? _ref5 : {
        url: ''
    });
    const accountingType = integrations.selectedTools.includes('toolQuickbooksOnline') ? Accounting.QBO : Accounting.Xero;
    const [integrationsSkipped, setIntegrationsSkipped] = useState((_ref6 = persistedFormState === null || persistedFormState === void 0 ? void 0 : persistedFormState.integrationsSkipped) !== null && _ref6 !== void 0 ? _ref6 : false);
    const skipInviteTeam = businessDetailsB.teamSize === 'just_me';
    const skipQboXero = integrationsSkipped || !integrations.selectedTools.includes('toolQuickbooksOnline') && !integrations.selectedTools.includes('toolXero');
    const skipSecureLogin = useStore(selectors.user.hasPassword);
    const { currentStep, progress, isSkippableStep, isBackDisabled, goNext, goBack } = useOnboardingFlow({
        mode,
        skipInviteTeam,
        skipQboXero,
        skipSecureLogin
    });
    const { mobile: isMobile } = useMediaQueries();
    const worksAt = (_useStore = useStore(selectors.user.business)) !== null && _useStore !== void 0 ? _useStore : null;
    const brandStepRef = useRef(null);
    const { api, logBI, setAuthToken } = useStoreActions();
    // Swap immediately so the new vendor's claims and userInfo are in the store before the
    // user moves past Business Details B. If the page is refreshed mid-onboarding, the auth
    // cookie now points at the new business (with requireVendorDetailsCompletion set), so
    // HomePage re-opens this modal instead of dropping the user on their existing dashboard.
    // Regression: ANCR-962.
    const handleAuthTokenReceived = useCallback(async (authToken)=>{
        await setAuthToken(authToken);
        queryClient.clear();
    }, [
        setAuthToken
    ]);
    const handleOnboardingComplete = useCallback(async ()=>{
        clearPersistedOnboardingProgress();
        const businessId = worksAt === null || worksAt === void 0 ? void 0 : worksAt.id;
        if (businessId != null) {
            const authToken = await api.user.switchBusiness(businessId);
            if (authToken != null) {
                await setAuthToken(authToken);
            }
        }
        onComplete();
    }, [
        api,
        worksAt === null || worksAt === void 0 ? void 0 : worksAt.id,
        setAuthToken,
        onComplete
    ]);
    const submitRef = useRef(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isIntegrationConnected, setIsIntegrationConnected] = useState((_ref7 = persistedFormState === null || persistedFormState === void 0 ? void 0 : persistedFormState.isIntegrationConnected) !== null && _ref7 !== void 0 ? _ref7 : false);
    const [hasCreatedBusiness, setHasCreatedBusiness] = useState((_ref8 = persistedFormState === null || persistedFormState === void 0 ? void 0 : persistedFormState.hasCreatedBusiness) !== null && _ref8 !== void 0 ? _ref8 : false);
    const [createdBusinessId, setCreatedBusinessId] = useState((_ref9 = persistedFormState === null || persistedFormState === void 0 ? void 0 : persistedFormState.createdBusinessId) !== null && _ref9 !== void 0 ? _ref9 : null);
    const [lastSubmittedBusinessSignature, setLastSubmittedBusinessSignature] = useState((_ref10 = persistedFormState === null || persistedFormState === void 0 ? void 0 : persistedFormState.lastSubmittedBusinessSignature) !== null && _ref10 !== void 0 ? _ref10 : null);
    const [creditsAwarded, setCreditsAwarded] = useState(()=>readPersistedCreditsAwarded());
    const [direction, setDirection] = useState('forward');
    const [extractedBranding, setExtractedBranding] = useState((_ref11 = persistedFormState === null || persistedFormState === void 0 ? void 0 : persistedFormState.extractedBranding) !== null && _ref11 !== void 0 ? _ref11 : null);
    const [branding, setBrandingState] = useState((_ref12 = persistedFormState === null || persistedFormState === void 0 ? void 0 : persistedFormState.branding) !== null && _ref12 !== void 0 ? _ref12 : computeInitialBranding(worksAt, (_ref13 = persistedFormState === null || persistedFormState === void 0 ? void 0 : persistedFormState.extractedBranding) !== null && _ref13 !== void 0 ? _ref13 : null));
    const [brandLogo, setBrandLogo] = useState(()=>{
        var _ref, _ref1;
        return {
            file: null,
            asDataURL: (_ref = persistedFormState === null || persistedFormState === void 0 ? void 0 : persistedFormState.brandLogoDataURL) !== null && _ref !== void 0 ? _ref : computeInitialLogoDataURL(worksAt, (_ref1 = persistedFormState === null || persistedFormState === void 0 ? void 0 : persistedFormState.extractedBranding) !== null && _ref1 !== void 0 ? _ref1 : null)
        };
    });
    const isWaitingOrClosing = currentStep === 'waiting' || currentStep === 'closing';
    const showCloseButton = (()=>{
        if (mode === 'clientToVendor') {
            return currentStep === 'intro';
        }
        if (mode === 'additionalAccount') {
            return currentStep === 'businessDetailsA';
        }
        return false;
    })();
    useEffect(()=>{
        setIsSubmitting(false);
    }, [
        currentStep
    ]);
    useEffect(()=>{
        persistCreditsAwarded(creditsAwarded);
    }, [
        creditsAwarded
    ]);
    useEffect(()=>{
        persistFormState({
            businessDetailsA,
            businessDetailsB,
            intent,
            integrations,
            inviteTeam,
            website,
            integrationsSkipped,
            isIntegrationConnected,
            hasCreatedBusiness,
            createdBusinessId,
            lastSubmittedBusinessSignature,
            extractedBranding,
            branding,
            brandLogoDataURL: brandLogo.asDataURL
        });
    }, [
        businessDetailsA,
        businessDetailsB,
        intent,
        integrations,
        inviteTeam,
        website,
        integrationsSkipped,
        isIntegrationConnected,
        hasCreatedBusiness,
        createdBusinessId,
        lastSubmittedBusinessSignature,
        extractedBranding,
        branding,
        brandLogo.asDataURL
    ]);
    const goForward = useCallback(()=>{
        setDirection('forward');
        setTimeout(()=>goNext(), 0);
    }, [
        goNext
    ]);
    const goBackward = useCallback(()=>{
        setDirection('back');
        setTimeout(()=>goBack(), 0);
    }, [
        goBack
    ]);
    const stepConfig = useMemo(()=>({
            businessDetailsA: {
                title: texts.businessDetailsAStep.title(),
                subtitle: texts.businessDetailsAStep.subtitle()
            },
            businessDetailsB: {
                title: texts.businessDetailsBStep.title(),
                subtitle: texts.businessDetailsBStep.subtitle()
            },
            brand: {},
            intent: {
                title: texts.intentStep.title(),
                subtitle: texts.intentStep.subtitle()
            },
            integrations: {
                title: texts.integrationsStep.title(),
                subtitle: texts.integrationsStep.subtitle()
            },
            inviteTeam: {
                title: texts.inviteTeamStep.title(),
                subtitle: texts.inviteTeamStep.subtitle()
            },
            businessWebsite: {
                title: texts.websiteStep.title(),
                subtitle: texts.websiteStep.subtitle()
            },
            qboXeroIntegration: {
                title: accountingType === Accounting.QBO ? texts.integrationConnectionStep.titleQbo() : texts.integrationConnectionStep.titleXero(),
                subtitle: texts.integrationConnectionStep.subtitle()
            },
            secureLogin: {
                title: texts.secureLoginStep.title(),
                subtitle: texts.secureLoginStep.subtitle()
            }
        }), [
        accountingType,
        texts
    ]);
    const handleIntroWatched = ()=>{
        setCreditsAwarded(true);
        goForward();
    };
    const handleLayoutContinue = ()=>{
        if (currentStep === 'integrations') {
            setIntegrationsSkipped(false);
        }
        if (currentStep === 'brand') {
            void (async ()=>{
                if (brandStepRef.current) {
                    const saved = await brandStepRef.current.save();
                    if (!saved) return;
                }
                goForward();
            })();
            return;
        }
        const handle = submitRef.current;
        if (handle != null) {
            void handle.submit();
            return;
        }
        goForward();
    };
    const handleLayoutSkip = ()=>{
        // TODO(ANC-13381): replace with real BI event id once assigned
        logBI(99999, {
            step: currentStep,
            action: 'skip'
        });
        if (currentStep === 'integrations') {
            setIntegrationsSkipped(true);
        }
        goForward();
    };
    const handleWebsiteContinue = (extracted)=>{
        setExtractedBranding(extracted);
        if (extracted != null) {
            setBrandingState(computeInitialBranding(worksAt, extracted));
            setBrandLogo({
                file: null,
                asDataURL: computeInitialLogoDataURL(worksAt, extracted)
            });
        }
        goForward();
    };
    const renderStepContent = ()=>{
        switch(currentStep){
            case 'businessDetailsA':
                return /*#__PURE__*/ _jsx(BusinessDetailsAStep, {
                    values: businessDetailsA,
                    onChange: setBusinessDetailsA,
                    onContinue: goForward,
                    submitRef: submitRef
                });
            case 'businessDetailsB':
                return /*#__PURE__*/ _jsx(BusinessDetailsBStep, {
                    valuesFromStepA: businessDetailsA,
                    values: businessDetailsB,
                    onChange: setBusinessDetailsB,
                    onContinue: goForward,
                    submitRef: submitRef,
                    onSubmittingChange: setIsSubmitting,
                    hasCreatedBusiness: hasCreatedBusiness,
                    createdBusinessId: createdBusinessId,
                    onBusinessCreated: (businessId)=>{
                        setHasCreatedBusiness(true);
                        setCreatedBusinessId(businessId);
                    },
                    onAuthTokenReceived: handleAuthTokenReceived,
                    lastSubmittedSignature: lastSubmittedBusinessSignature,
                    onSubmitted: setLastSubmittedBusinessSignature,
                    entitleOnboardingVideoCredit: creditsAwarded
                });
            case 'intent':
                return /*#__PURE__*/ _jsx(IntentStep, {
                    values: intent,
                    onChange: setIntent,
                    onContinue: goForward,
                    submitRef: submitRef
                });
            case 'integrations':
                return /*#__PURE__*/ _jsx(IntegrationsStep, {
                    values: integrations,
                    onChange: setIntegrations,
                    onContinue: goForward,
                    submitRef: submitRef
                });
            case 'inviteTeam':
                return /*#__PURE__*/ _jsx(InviteTeamStep, {
                    values: inviteTeam,
                    onChange: setInviteTeam,
                    onContinue: goForward,
                    submitRef: submitRef,
                    onSubmittingChange: setIsSubmitting
                });
            case 'businessWebsite':
                return /*#__PURE__*/ _jsx(WebsiteStep, {
                    values: website,
                    onChange: setWebsite,
                    onContinue: handleWebsiteContinue,
                    onSubmittingChange: setIsSubmitting,
                    submitRef: submitRef
                });
            case 'qboXeroIntegration':
                return /*#__PURE__*/ _jsx(QboXeroIntegrationStep, {
                    accountingType: accountingType,
                    connected: isIntegrationConnected,
                    onContinue: goForward,
                    onConnectingChange: setIsSubmitting,
                    onConnectedChange: setIsIntegrationConnected,
                    submitRef: submitRef
                });
            case 'brand':
                return /*#__PURE__*/ _jsx(BrandStep, {
                    ref: brandStepRef,
                    worksAt: worksAt,
                    branding: branding,
                    onBrandingChange: setBrandingState,
                    logo: brandLogo,
                    onLogoChange: setBrandLogo
                });
            case 'secureLogin':
                return /*#__PURE__*/ _jsx(SecureLoginStep, {
                    onContinue: goForward,
                    onSubmittingChange: setIsSubmitting,
                    submitRef: submitRef
                });
            default:
                return null;
        }
    };
    const config = stepConfig[currentStep];
    return /*#__PURE__*/ _jsx(Box, {
        className: clsx(styles.root, {
            [styles.mobile]: isMobile,
            [styles.dashboardBackground]: isWaitingOrClosing
        }),
        testId: testIds.VendorOnboardingTestIds.Root(),
        children: /*#__PURE__*/ _jsxs(Card, {
            bi: {
                name: 'vendor_onboarding_card'
            },
            className: styles.card,
            borderSkin: "default",
            borderRadius: "m",
            children: [
                currentStep === 'intro' && /*#__PURE__*/ _jsx(IntroStep, {
                    onSkip: goForward,
                    onComplete: handleIntroWatched,
                    onClose: showCloseButton ? onClose : undefined,
                    creditsAlreadyAwarded: creditsAwarded
                }),
                currentStep === 'waiting' && /*#__PURE__*/ _jsx(WaitingStep, {
                    onComplete: goNext
                }),
                currentStep === 'closing' && /*#__PURE__*/ _jsx(ClosingStep, {
                    onComplete: handleOnboardingComplete
                }),
                currentStep !== 'intro' && currentStep !== 'waiting' && currentStep !== 'closing' && /*#__PURE__*/ _jsx(OnboardingLayout, {
                    progress: progress,
                    onBack: goBackward,
                    onContinue: handleLayoutContinue,
                    onSkip: handleLayoutSkip,
                    onClose: showCloseButton ? onClose : undefined,
                    showSkipButton: isSkippableStep || currentStep === 'qboXeroIntegration' && !isIntegrationConnected,
                    continueLabel: currentStep === 'qboXeroIntegration' && !isIntegrationConnected ? accountingType === Accounting.QBO ? texts.integrationConnectionStep.connectQbo() : texts.integrationConnectionStep.connectXero() : undefined,
                    skipLabel: currentStep === 'qboXeroIntegration' && !isIntegrationConnected ? texts.integrationConnectionStep.connectLater() : undefined,
                    isContinueDisabled: isSubmitting,
                    isContinueLoading: isSubmitting,
                    isBackDisabled: isBackDisabled || isSubmitting,
                    children: /*#__PURE__*/ _jsx(AnimatePresence, {
                        mode: "wait",
                        custom: direction,
                        initial: false,
                        children: /*#__PURE__*/ _jsxs(motion.div, {
                            custom: direction,
                            initial: (dir)=>({
                                    x: dir === 'forward' ? 50 : -50,
                                    opacity: 0
                                }),
                            animate: {
                                x: 0,
                                opacity: 1
                            },
                            exit: (dir)=>({
                                    x: dir === 'forward' ? -50 : 50,
                                    opacity: 0
                                }),
                            transition: {
                                duration: 0.4,
                                ease: 'easeInOut'
                            },
                            className: clsx(styles.animatedStep, currentStep === 'brand' && styles.fullWidthStep, currentStep === 'inviteTeam' && styles.topAligned),
                            children: [
                                ((config === null || config === void 0 ? void 0 : config.title) || (config === null || config === void 0 ? void 0 : config.subtitle)) && /*#__PURE__*/ _jsxs("div", {
                                    className: styles.titleBlock,
                                    children: [
                                        (config === null || config === void 0 ? void 0 : config.title) && /*#__PURE__*/ _jsx(Typography, {
                                            variant: "h2",
                                            weight: "bold",
                                            className: styles.title,
                                            children: config.title
                                        }),
                                        (config === null || config === void 0 ? void 0 : config.subtitle) && /*#__PURE__*/ _jsx(Typography, {
                                            variant: "paragraph2",
                                            className: styles.subtitle,
                                            children: config.subtitle
                                        })
                                    ]
                                }),
                                renderStepContent()
                            ]
                        }, currentStep)
                    })
                })
            ]
        })
    });
};
